import React from 'react';
import { useFormContext } from 'react-hook-form';

import { escapeRegex } from '@/utils/text';
import { Checkbox } from '@/components/Form/components/Checkbox/Checkbox';
import { Overline } from '@/components/Typography/Typography';
import { MultiSelectOption } from '@/components/Form/components/MultiSelect/types';
import { StyledMultiSelectRow } from '@/components/Form/components/MultiSelect/styles';
import { StyledIcon } from '@/components/Form/components/Input/styles';
import { HandleNumber, HandleString } from '@/components/Form/types';
import { Icon } from '@/components/Icon';

interface MultiSelectRowProps {
  name: string;
  options: MultiSelectOption[];
  valueAsNumber?: boolean;
  isMultiple?: boolean;
  searchString?: string;
  getOptionId: (index: number) => string;
  handleSideEffect?: HandleString | HandleNumber;
  onSingleSelect: (value: string | number) => void;
  onOptionKeyDown: (e: React.KeyboardEvent<HTMLDivElement>, value: string | number) => void;
  onOptionFocus: (index: number) => void;
}

export const MultiSelectRows = ({
  name,
  options,
  searchString = '',
  valueAsNumber,
  isMultiple,
  getOptionId,
  handleSideEffect,
  onSingleSelect,
  onOptionKeyDown,
  onOptionFocus,
}: MultiSelectRowProps): JSX.Element | null => {
  const { watch } = useFormContext();
  const watched = watch(name);

  const regex = new RegExp(escapeRegex(searchString), 'i');
  const getRegexedLabel = (label: string) => (
    <span
      dangerouslySetInnerHTML={{
        __html: label.replace(regex, `<strong>$&</strong>`),
      }}
    />
  );

  return options.length ? (
    <>
      {options.map(({ isHeader, isSubHeader, value, label }, i) =>
        isHeader || isSubHeader ? (
          <Overline key={i} subLevel={isSubHeader} data-testid="option" aria-label={label}>
            {label}
          </Overline>
        ) : isMultiple ? (
          <Checkbox
            key={i}
            data-testid="option"
            name={`${name}.${i}`}
            value={value}
            valueAsNumber={valueAsNumber}
            fullwidth
            handleSideEffect={handleSideEffect}
          >
            {getRegexedLabel(label)}
          </Checkbox>
        ) : (
          <StyledMultiSelectRow
            key={i}
            as="div"
            id={getOptionId(i)}
            role="option"
            data-testid="option"
            aria-label={label}
            aria-selected={watched === value}
            isSelected={watched === value}
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => onOptionKeyDown(e, value)}
            onFocus={() => onOptionFocus(i)}
            onClick={() => onSingleSelect(value)}
          >
            {getRegexedLabel(label)}
            {watched === value ? (
              <StyledIcon>
                <Icon icon="check" fixedWidth size="lg" />
              </StyledIcon>
            ) : null}
          </StyledMultiSelectRow>
        ),
      )}
    </>
  ) : null;
};
